import { MultiValue, PropsValue } from "chakra-react-select";
import { GenericSelectData } from "gen/model/genericSelectData";
import { memoize } from "lodash-es";
import React from "react";
import {
  SelectGroup,
  SelectGroupProps,
  SelectOption,
  SelectOptionProps,
} from "shared/components/input/select/SelectOption";
import {
  SelectGroupData,
  SelectOptionData,
} from "shared/components/input/select/SelectTypes";
import { FormInputValue } from "shared/types/FormInputValue";
import reactToText from "shared/utils/ReactToText";
import {
  isElementOfType,
  isValidElementWithChildren,
} from "shared/utils/ReactUtils";

export const isSelectMultiValue = (
  option: PropsValue<SelectOptionData>,
): option is MultiValue<SelectOptionData> => Array.isArray(option);

// __isNew__ is used internally by react-select, see:
// https://github.com/JedWatson/react-select/blob/b284329ea92a951725fdf1ff48e6432eda545b5c/packages/react-select/src/useCreatable.ts#L101
export const isSelectNewOptionData = (
  data: unknown,
): data is {
  label: string;
  value: never;
  __isNew__: true;
} =>
  typeof data === "object" &&
  data !== null &&
  "__isNew__" in data &&
  data.__isNew__ === true;

export const getSelectOptionsFromChildren = <T extends FormInputValue = string>(
  children: React.ReactNode,
): (SelectOptionData | SelectGroupData)[] =>
  React.Children.toArray(children)
    .flatMap((child) =>
      isSelectOption(child)
        ? ({
            label: reactToText(child.props.children) || child.props.label || "",
            value: child.props.value,
            isDisabled: child.props.isDisabled,
            labelRight: child.props.labelRight,
          } as SelectOptionData)
        : isSelectGroup(child)
          ? ({
              label: child.props.label,
              options: getSelectOptionsFromChildren<T>(child.props.children),
            } as SelectGroupData)
          : isValidElementWithChildren(child) && child.props.children
            ? getSelectOptionsFromChildren(child.props.children)
            : [],
    )
    .filter(Boolean);

const isSelectOption = (
  child: React.ReactNode,
): child is React.ReactElement<SelectOptionProps> =>
  React.isValidElement(child) && isElementOfType(child, SelectOption);

const isSelectGroup = (
  child: React.ReactNode,
): child is React.ReactElement<SelectGroupProps> =>
  React.isValidElement(child) && isElementOfType(child, SelectGroup);

export const isSelectOptionData = (value: unknown): value is SelectOptionData =>
  typeof value === "object" && value !== null && "value" in value;

const isSelectGroupData = (value: unknown): value is SelectGroupData =>
  typeof value === "object" && value !== null && "options" in value;

export const flattenSelectOptions = (
  options: readonly (SelectOptionData | SelectGroupData)[],
): SelectOptionData[] =>
  options.flatMap((option) =>
    isSelectGroupData(option)
      ? flattenSelectOptions(option.options)
      : isSelectOptionData(option)
        ? option
        : [],
  );

export const selectOptionsFromGenericSelectData = memoize(
  (data: GenericSelectData[] | undefined) =>
    data?.map((item) => (
      <SelectOption key={item.value} value={item.value}>
        {item.rich_value}
      </SelectOption>
    )) ?? null,
);

export const selectValueIsMulti = <
  T extends FormInputValue = string,
  IsMulti extends boolean = false,
>(
  value: T | T[] | null | undefined,
  isMulti: IsMulti | undefined,
): value is T[] => isMulti === true;
